// VacationCountdownTile.jsx
//
// Compact "Vacation Countdown" tile for the dashboard — the tile next to the
// weather tile. Shows days remaining until the customer's saved Disney
// vacation date, over a fireworks background.
//
// This does NOT re-implement the full VacationCountdown screen. It reuses the
// exact same day-diff that screen's getDaysLeft() uses — moment-timezone diff
// of (countDownDate) vs (currentDate) in the customer's timezone. Those three
// values come from the sub_info_update socket data in App.js and are passed
// down through Dashboard, identical to how the Home and VacationCountdown
// screens already receive them.
//
// BACKGROUND IMAGE: /vacationfw.jpg must be a small, web-sized file
// (~600x400, ~34KB). An earlier multi-MB version blocked the main thread on
// slow Android displays and stalled the dashboard animations. The <img> also
// uses decoding="async" so the decode can never block rendering.
//
// ─── Props ────────────────────────────────────────────────────────────────
//   countDownDate  customer's vacation date (from cf_disney_vacation_countdown)
//   timezone       customer's tz (already normalized in App.js, e.g. EST5EDT)
//   currentDate    today's date string in that tz (YYYY-MM-DD), from App.js
//   style          (optional) merged onto the outer tile container.
//
// Empty state ("No Trip Planned") shows when no vacation date is set.
//
// REQUIRES in /public:  /vacationfw.jpg  (small fireworks background image)

import moment from "moment-timezone";

// Same logic as VacationCountdown screen's getDaysLeft().
function getDaysLeft(countDownDate, currentDate, timezone) {
  if (!countDownDate || !timezone) return null;
  const countdownEnd = moment(countDownDate).tz(timezone);
  const current = moment(currentDate).tz(timezone);
  let dl = countdownEnd.diff(current, "days");
  if (dl < 0) dl = 0;
  return dl;
}

const TEXT_SHADOW = "0 2px 8px rgba(0,0,0,0.65)";

const VacationCountdownTile = ({
  countDownDate,
  timezone,
  currentDate,
  style,
}) => {
  const daysLeft = getDaysLeft(countDownDate, currentDate, timezone);
  const hasTrip = daysLeft !== null;

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        borderRadius: 20,
        overflow: "hidden",
        border: "1px solid rgba(11,31,53,0.12)",
        boxShadow: "0 10px 28px rgba(11,31,53,0.16)",
        background: "#0b1f4d",
        ...style,
      }}
    >
      {/* Fireworks background — anchored to the top so the bursts stay
          visible and the empty lower sky is what gets cropped.
          decoding="async" keeps the decode off the critical path. */}
      <img
        src="/vacationfw.jpg"
        alt=""
        decoding="async"
        loading="lazy"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          objectPosition: "top",
        }}
      />

      {/* Light scrim — near-nothing at the top so the fireworks pop,
          slightly darker lower down to keep the number crisp. */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(180deg, rgba(8,16,48,0.05) 0%, rgba(8,16,48,0.28) 55%, rgba(8,16,48,0.42) 100%)",
          pointerEvents: "none",
        }}
      />

      {/* Content */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "14px 12px",
          fontFamily: "'Lato', system-ui, -apple-system, sans-serif",
          textAlign: "center",
        }}
      >
        {hasTrip ? (
          <>
            <div
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: "#ffffff",
                opacity: 0.92,
                textShadow: TEXT_SHADOW,
                marginBottom: 2,
              }}
            >
              Vacation Countdown
            </div>
            <div
              style={{
                fontSize: 56,
                fontWeight: 800,
                lineHeight: 1,
                color: "#ffffff",
                textShadow: TEXT_SHADOW,
              }}
            >
              {daysLeft}
            </div>
            <div
              style={{
                fontSize: 15,
                fontWeight: 700,
                color: "#ffffff",
                opacity: 0.92,
                textShadow: TEXT_SHADOW,
                marginTop: 2,
              }}
            >
              {daysLeft === 1 ? "Day to Go" : "Days to Go"}
            </div>
          </>
        ) : (
          <>
            <div
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: "#ffffff",
                opacity: 0.92,
                textShadow: TEXT_SHADOW,
                marginBottom: 6,
              }}
            >
              Vacation Countdown
            </div>
            <div
              style={{
                fontSize: 20,
                fontWeight: 800,
                color: "#ffffff",
                textShadow: TEXT_SHADOW,
              }}
            >
              No Trip Planned
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VacationCountdownTile;
